import React, { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { DesktopService } from '../services/desktopService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

const SparklesIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
);

const LoaderIcon = ({ className }) => (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const DescriptionModal = ({ isOpen, onClose, nodeId, initialDescription, userId, deviceId, folderPath }) => {
    const [description, setDescription] = useState(initialDescription || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [status, setStatus] = useState('');

    useEffect(() => {
        if (isOpen) {
            setDescription(initialDescription || '');
        }
    }, [isOpen, initialDescription]);

    const handleSave = async () => {
        try {
            setIsSaving(true);
            const nodeRef = doc(db, 'nodes', nodeId);
            await updateDoc(nodeRef, {
                'settings.customInstructions': description, // Assuming description maps to customInstructions as per instructions
                updatedAt: serverTimestamp()
            });
            onClose();
        } catch (error) {
            console.error("Error saving description:", error);
            alert("Failed to save description.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleGenerate = async () => {
        console.log("🚀 Starting Context Generation...");
        if (!deviceId || !folderPath) {
            alert("This node does not have a linked desktop folder. Please configure the target device and path in Space Settings.");
            return;
        }

        try {
            setIsGenerating(true);
            setStatus('Scanning project files...');
            console.log("📂 Sending 'get_project_context' command for path:", folderPath);

            // 1. Trigger Agent Scan
            const taskId = await DesktopService.sendDesktopCommand(userId, deviceId, {
                command: 'get_project_context',
                path: folderPath
            });
            console.log("✅ Task sent, ID:", taskId);

            // 2. Poll for results
            const contextData = await new Promise((resolve, reject) => {
                const unsubscribe = DesktopService.subscribeToTask(userId, deviceId, taskId, (response) => {
                    console.log("📡 Agent message:", response ? "Data received" : "Waiting...");
                    if (response) {
                        unsubscribe();
                        try {
                            const parsed = typeof response === 'string' ? JSON.parse(response) : response;
                            if (parsed.error) {
                                console.error("❌ Agent error:", parsed.error);
                                reject(new Error(parsed.error));
                            } else {
                                resolve(parsed);
                            }
                        } catch (e) {
                            reject(new Error("Failed to parse agent response"));
                        }
                    }
                });

                // Timeout after 30s
                setTimeout(() => {
                    unsubscribe();
                    reject(new Error("Scanner timed out (30s)"));
                }, 30000);
            });

            setStatus('Analyzing context with AI...');
            console.log("🧠 Invoking 'generateProjectSummary'...");

            // 3. Call Cloud Function for Summarization
            const generateSummary = httpsCallable(functions, 'generateProjectSummary');
            const result = await generateSummary({ contextData });

            console.log("✅ Summary generated");
            setDescription(result.data.summary);
            setStatus('');
            setIsGenerating(false);
        } catch (error) {
            console.error("❌ Generation Error:", error);
            setStatus(`Error: ${error.message}`);
            setIsGenerating(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-[var(--bg-main)] border border-[var(--border-subtle)] rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="px-6 py-5 border-b border-[var(--border-subtle)] flex items-center justify-between bg-[var(--bg-secondary)]/30">
                    <div>
                        <h2 className="text-xl font-bold text-[var(--text-main)]">Project Description</h2>
                        <p className="text-xs text-[var(--text-muted)] mt-0.5 font-medium uppercase tracking-wider">Context & Instructions</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l18 18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 flex-1 overflow-y-auto">
                    <div className="relative group">
                        <textarea
                            autoFocus
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe what this space is about, the technology stack, and any specific rules for the AI experts here..."
                            className="w-full h-64 bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] rounded-2xl p-4 text-[var(--text-main)] text-sm leading-relaxed outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500/50 transition-all resize-none shadow-inner custom-scrollbar"
                            disabled={isGenerating || isSaving}
                        />

                        {isGenerating && (
                            <div className="absolute inset-0 bg-[var(--bg-tertiary)]/80 backdrop-blur-[2px] rounded-2xl flex flex-col items-center justify-center animate-in fade-in">
                                <LoaderIcon className="w-8 h-8 text-indigo-500 mb-3" />
                                <p className="text-sm font-medium text-[var(--text-main)] animate-pulse">{status}</p>
                            </div>
                        )}

                        {!isGenerating && status && (
                            <div className="mt-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-medium animate-in slide-in-from-top-2">
                                {status}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                        <button
                            onClick={handleGenerate}
                            disabled={isGenerating || isSaving}
                            className={`
                                flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
                                ${isGenerating
                                    ? 'bg-indigo-500/20 text-indigo-400 cursor-not-allowed'
                                    : 'bg-indigo-600/10 text-indigo-400 hover:bg-indigo-600/20 border border-indigo-500/20 hover:border-indigo-500/40 shadow-sm'}
                            `}
                        >
                            <SparklesIcon className={`w-4 h-4 ${isGenerating ? 'animate-pulse' : ''}`} />
                            {isGenerating ? 'Processing...' : 'Generate Context from Files'}
                        </button>

                        <div className="text-[11px] text-[var(--text-muted)] italic max-w-[200px] text-right">
                            {deviceId ? `Connected to: ${deviceId.slice(0, 8)}...` : 'No desktop linked'}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-[var(--bg-secondary)]/50 border-t border-[var(--border-subtle)] flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving || isGenerating}
                        className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2"
                    >
                        {isSaving && <LoaderIcon className="w-4 h-4" />}
                        {isSaving ? 'Saving...' : 'Update Context'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DescriptionModal;
